import { getCurrentTenantId } from '@/lib/auth-utils';
import { supabase } from './supabase';

/**
 * Conversation interface for the conversations table
 */
export interface Conversation {
  conversation_id: string;
  story_id: number | string;
  project_id?: number | string | null;
  created_at: string;
  last_active_at: string;
  summary?: string | null;
  status?: string | null;
  tenant_id: string;
  name?: string | null;
}

/**
 * Get all conversations for a story
 * @param storyId - Story ID
 * @param projectId - Optional Project ID to scope conversations
 * @returns Promise with array of conversations
 */
export const getConversations = async (storyId: string | number, projectId?: string | number): Promise<Conversation[]> => {
  try {
    // Determine if we should parse as int (for serial IDs) or keep as string (for UUIDs)
    const isStoryIdNumber = !isNaN(Number(storyId)) && typeof storyId !== 'object' && String(storyId).length < 10;
    const parsedStoryId = isStoryIdNumber ? Number(storyId) : storyId;

    const tenantId = await getCurrentTenantId();

    console.log('[getConversations] params:', { storyId, parsedStoryId, projectId, tenantId });

    if (!tenantId) {
      console.warn('No tenant context found for conversations');
      return [];
    }

    let query = supabase
      .from('conversations')
      .select('*')
      .eq('story_id', parsedStoryId);

    const { data, error } = await query.order('last_active_at', { ascending: false });

    if (error) {
      console.error('Error fetching conversations:', error);
      throw error;
    }

    // Deduplicate on client side just in case
    const uniqueConversations = data ? Array.from(new Map(data.map(item => [item.conversation_id, item])).values()) : [];

    return uniqueConversations;
  } catch (error) {
    console.error('Error in getConversations:', error);
    throw error;
  }
};

/**
 * Get a single conversation
 * @param conversationId - Conversation ID
 * @returns Promise with conversation data
 */
export const getConversation = async (conversationId: string): Promise<Conversation | null> => {
  try {
    const { data, error } = await supabase
      .from('conversations')
      .select('*')
      .eq('conversation_id', conversationId)
      .single();

    if (error) {
      console.error('Error fetching conversation:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in getConversation:', error);
    throw error;
  }
};

/**
 * Create a new conversation
 * @param storyId - Story ID
 * @param name - Conversation name (optional)
 * @returns Promise with created conversation
 */
export const createConversation = async (
  storyId: string | number,
  localId: string,
  projectId?: string | number,
  name?: string
): Promise<Conversation> => {
  try {
    const parsedStoryId = typeof storyId === 'string' ? parseInt(storyId) : storyId;
    const tenantId = await getCurrentTenantId();

    if (!tenantId) {
      throw new Error('Unable to determine tenant context');
    }

    const payload: any = {
      story_id: parsedStoryId,
      tenant_id: tenantId,
      name: name || 'New Conversation',
      status: 'active',
      local_id: localId,
      last_active_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('conversations')
      .upsert(payload, { onConflict: 'local_id' })
      .select()
      .single();

    if (error) {
      console.error('Error creating conversation:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in createConversation:', error);
    throw error;
  }
};

/**
 * Update conversation's last active time
 * @param conversationId - Conversation ID
 * @returns Promise with updated conversation
 */
export const updateConversationActivity = async (conversationId: string): Promise<Conversation> => {
  try {
    const { data, error } = await supabase
      .from('conversations')
      .update({
        last_active_at: new Date().toISOString()
      })
      .eq('conversation_id', conversationId)
      .select()
      .single();

    if (error) {
      console.error('Error updating conversation activity:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in updateConversationActivity:', error);
    throw error;
  }
};

/**
 * Update conversation name
 * @param conversationId - Conversation ID
 * @param name - New name
 * @returns Promise with updated conversation
 */
export const updateConversationName = async (
  conversationId: string,
  name: string
): Promise<Conversation> => {
  try {
    const { data, error } = await supabase
      .from('conversations')
      .update({
        name,
        last_active_at: new Date().toISOString()
      })
      .eq('conversation_id', conversationId)
      .select()
      .single();

    if (error) {
      console.error('Error updating conversation name:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in updateConversationName:', error);
    throw error;
  }
};

/**
 * Archive a conversation
 * @param conversationId - Conversation ID
 * @returns Promise with updated conversation
 */
export const archiveConversation = async (conversationId: string): Promise<Conversation> => {
  try {
    const { data, error } = await supabase
      .from('conversations')
      .update({
        status: 'archived',
        last_active_at: new Date().toISOString()
      })
      .eq('conversation_id', conversationId)
      .select()
      .single();

    if (error) {
      console.error('Error archiving conversation:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in archiveConversation:', error);
    throw error;
  }
};

/**
 * Unarchive a conversation
 * @param conversationId - Conversation ID
 * @returns Promise with updated conversation
 */
export const unarchiveConversation = async (conversationId: string): Promise<Conversation> => {
  try {
    const { data, error } = await supabase
      .from('conversations')
      .update({
        status: 'active',
        last_active_at: new Date().toISOString()
      })
      .eq('conversation_id', conversationId)
      .select()
      .single();

    if (error) {
      console.error('Error unarchiving conversation:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in unarchiveConversation:', error);
    throw error;
  }
};

/**
 * Delete a conversation
 * @param conversationId - Conversation ID
 * @returns Promise
 */
export const deleteConversation = async (conversationId: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('conversations')
      .delete()
      .eq('conversation_id', conversationId);

    if (error) {
      console.error('Error deleting conversation:', error);
      throw error;
    }
  } catch (error) {
    console.error('Error in deleteConversation:', error);
    throw error;
  }
};
